import React, { useEffect, useMemo, useState } from "react";
import { Info, Save, X } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";

export const AUTO_GREETING_COOLDOWN_OPTIONS = [
  { label: "30 minutes", value: 30 },
  { label: "1 hour", value: 60 },
  { label: "6 hours", value: 360 },
  { label: "12 hours", value: 720 },
  { label: "24 hours", value: 1440 },
  { label: "7 days", value: 10080 },
];

const DEFAULT_GREETING_MESSAGE =
  "Thank you for contacting us. Our team will contact you soon.";

const defaultSettings = {
  isAutoGreetingEnabled: false,
  autoGreetingMessage: "",
  autoGreetingCooldownMinutes: 1440,
};

function normalizeSettings(data) {
  return {
    isAutoGreetingEnabled: !!data?.isAutoGreetingEnabled,
    autoGreetingMessage: data?.autoGreetingMessage || "",
    autoGreetingCooldownMinutes:
      Number(data?.autoGreetingCooldownMinutes) || 1440,
  };
}

function extractErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return data?.message || data?.error || data?.title || error?.message || fallback;
}

export default function AutoGreetingSettingsPanel({
  phoneNumberId,
  provider = "META_CLOUD",
  withBiz = cfg => cfg,
  senderLabel = "this sender",
}) {
  const [serverSettings, setServerSettings] = useState(defaultSettings);
  const [draft, setDraft] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const encodedPhoneNumberId = useMemo(
    () => encodeURIComponent((phoneNumberId || "").trim()),
    [phoneNumberId]
  );

  const endpoint = useMemo(
    () =>
      `/whatsappsettings/${provider}/numbers/${encodedPhoneNumberId}/auto-greeting`,
    [encodedPhoneNumberId, provider]
  );

  useEffect(() => {
    let mounted = true;

    async function loadSettings() {
      if (!encodedPhoneNumberId) {
        setLoading(false);
        setError("Save this sender with a Phone Number ID before configuring Auto Greeting.");
        return;
      }

      try {
        setLoading(true);
        setError("");
        const { data } = await axiosClient.get(endpoint, withBiz());
        if (!mounted) return;
        const normalized = normalizeSettings(data);
        setServerSettings(normalized);
        setDraft(normalized);
      } catch (err) {
        if (!mounted) return;
        setError(extractErrorMessage(err, "Failed to load Auto Greeting settings."));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSettings();

    return () => {
      mounted = false;
    };
  }, [encodedPhoneNumberId, endpoint, withBiz]);

  const validationError = useMemo(() => {
    const message = (draft.autoGreetingMessage || "").trim();
    const cooldown = Number(draft.autoGreetingCooldownMinutes);

    if (draft.isAutoGreetingEnabled && !message) {
      return "Auto greeting message is required when enabled.";
    }
    if ((draft.autoGreetingMessage || "").length > 4096) {
      return "Auto greeting message must be 4096 characters or fewer.";
    }
    if (!Number.isFinite(cooldown) || cooldown < 30 || cooldown > 43200) {
      return "Auto greeting cooldown must be between 30 and 43,200 minutes.";
    }
    return "";
  }, [draft]);

  const dirty = useMemo(
    () =>
      draft.isAutoGreetingEnabled !== serverSettings.isAutoGreetingEnabled ||
      (draft.autoGreetingMessage || "") !==
        (serverSettings.autoGreetingMessage || "") ||
      Number(draft.autoGreetingCooldownMinutes) !==
        Number(serverSettings.autoGreetingCooldownMinutes),
    [draft, serverSettings]
  );

  const disabled = loading || saving || !!validationError || !encodedPhoneNumberId;

  const handleSave = async () => {
    if (disabled) return;

    const payload = {
      isAutoGreetingEnabled: !!draft.isAutoGreetingEnabled,
      autoGreetingMessage: (draft.autoGreetingMessage || "").trim(),
      autoGreetingCooldownMinutes: Number(draft.autoGreetingCooldownMinutes),
    };

    try {
      setSaving(true);
      setError("");
      const { data } = await axiosClient.put(endpoint, payload, withBiz());
      const normalized = normalizeSettings(data || payload);
      setServerSettings(normalized);
      setDraft(normalized);
      toast.success("Auto greeting saved");
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to save Auto Greeting settings."));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(serverSettings);
    setError("");
  };

  return (
    <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-4 text-left">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h4 className="text-sm font-bold text-slate-800">Auto Greeting</h4>
          <p className="mt-1 text-[11px] font-medium leading-relaxed text-slate-500">
            Send a simple automatic reply when no active automation or flow handles the customer’s text message.
          </p>
          <p className="mt-1 text-[10px] font-semibold text-slate-400">
            Sender: {senderLabel || phoneNumberId}
          </p>
        </div>
        <label className="relative inline-flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={!!draft.isAutoGreetingEnabled}
            onChange={event =>
              setDraft(current => ({
                ...current,
                isAutoGreetingEnabled: event.target.checked,
              }))
            }
            disabled={loading || saving || !encodedPhoneNumberId}
            className="sr-only peer"
          />
          <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-emerald-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-disabled:cursor-not-allowed peer-disabled:opacity-60" />
          <span className="text-xs font-bold text-slate-700">Enable auto greeting</span>
        </label>
      </div>

      {loading ? (
        <div className="mt-4 rounded-lg border border-slate-100 bg-white px-3 py-2 text-xs font-semibold text-slate-500">
          Loading Auto Greeting settings…
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Message
              </label>
              <span
                className={`text-[10px] font-bold ${
                  (draft.autoGreetingMessage || "").length > 4096
                    ? "text-red-500"
                    : "text-slate-400"
                }`}
              >
                {(draft.autoGreetingMessage || "").length} / 4096
              </span>
            </div>
            <textarea
              value={draft.autoGreetingMessage || ""}
              onChange={event =>
                setDraft(current => ({
                  ...current,
                  autoGreetingMessage: event.target.value,
                }))
              }
              placeholder={DEFAULT_GREETING_MESSAGE}
              rows={4}
              className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition-all focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/5"
            />
          </div>

          <div className="max-w-xs">
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Send again after
            </label>
            <select
              aria-label="Send again after"
              value={draft.autoGreetingCooldownMinutes}
              onChange={event =>
                setDraft(current => ({
                  ...current,
                  autoGreetingCooldownMinutes: Number(event.target.value),
                }))
              }
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 outline-none transition-all focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/5"
            >
              {AUTO_GREETING_COOLDOWN_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 text-[11px] font-medium leading-relaxed text-blue-700">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>
              Text messages only in this version. Active chats, AutoReply flows, and Default Entry Flow still take priority.
            </span>
          </div>

          {(validationError || error) && (
            <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-[11px] font-bold text-red-600">
              {validationError || error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-bold text-slate-600 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X size={13} />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={disabled || !dirty}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white shadow-sm transition-all hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <Save size={13} />
              {saving ? "Saving…" : "Save Auto Greeting"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
